import type { KpiKey, KpiScoreEntry } from "@/lib/scoring/types";

export interface SimulatedKpiTarget {
  key: KpiKey;
  label: string;
  /** 0-10, wie bereits in KpiScoreEntry.basePoints. */
  currentBasePoints: number;
  /** 0-10, geklemmt - wie viele Punkte dieser EINE Bereich (alle anderen unverändert)
   * bräuchte, um das Gesamtziel zu erreichen. */
  neededBasePoints: number;
  /** neededBasePoints - currentBasePoints, nie negativ. */
  pointsNeeded: number;
  /** false, wenn selbst die volle Punktzahl (10/10) in diesem EINEN Bereich allein nicht
   * ausreicht - das Ziel bräuchte dann mehrere Bereiche gleichzeitig. */
  reachable: boolean;
}

/**
 * Score-Zusammensetzungs-Simulator ("was müsste sich ändern, damit der Score um X Punkte
 * steigt", backlog.md "Weiterer Nutzwert-Katalog") - rein rechnerisch-didaktisch, KEINE
 * KI-Schätzung und KEINE Handlungsempfehlung: nutzt ausschließlich die bereits von
 * `computeScore()` (lib/scoring/scoring.ts) berechnete 0-10-Punkte-Abstraktion je Bereich,
 * ohne zugrundeliegende reale Kennzahlen (Umsatzwachstum-%, Marge-% etc.) neu zu
 * interpretieren oder zu empfehlen, wie sie erreicht werden könnten.
 *
 * Mathematische Grundlage direkt aus `computeScore()`s eigener Formel abgeleitet:
 * `total = round((sum(basePoints) / count) * 10)`. Für JEDEN verfügbaren Bereich einzeln
 * (alle anderen unverändert) wird berechnet, welchen Wert `basePoints` mindestens
 * annehmen müsste, damit `total` das Ziel erreicht - `Math.round` rundet x.5 aufwärts,
 * daher reicht rechnerisch bereits `sum/count*10 >= target - 0.5`.
 *
 * Bewusst nur EIN Bereich gleichzeitig simuliert (kein kombinatorischer Multi-Bereich-
 * Optimierer) - deutlich einfacher nachvollziehbar ("Bereich X müsste auf Y/10 steigen")
 * als eine Kombination mehrerer gleichzeitiger Teiländerungen, und deckt bereits den
 * Kernnutzen ab ("welcher einzelne Hebel wäre am nächsten dran"). Bereiche, bei denen
 * selbst 10/10 nicht reicht (`reachable: false`), zeigen ehrlich, dass ein einzelner
 * Bereich das gewählte Ziel nicht allein tragen kann.
 */
export function simulateScoreIncrease(
  kpis: KpiScoreEntry[],
  currentTotal: number,
  deltaPoints: number,
): SimulatedKpiTarget[] {
  const available = kpis.filter(
    (k): k is KpiScoreEntry & { basePoints: number } => k.available && k.basePoints !== undefined,
  );
  if (available.length === 0 || deltaPoints <= 0) return [];

  const count = available.length;
  const currentSum = available.reduce((sum, k) => sum + k.basePoints, 0);
  const targetTotal = Math.min(100, currentTotal + deltaPoints);
  const requiredSum = ((targetTotal - 0.5) * count) / 10;

  return available
    .map((k) => {
      const sumWithoutK = currentSum - k.basePoints;
      const neededRaw = requiredSum - sumWithoutK;
      const neededBasePoints = Math.min(10, Math.max(k.basePoints, neededRaw));
      return {
        key: k.key,
        label: k.label,
        currentBasePoints: k.basePoints,
        neededBasePoints,
        pointsNeeded: Math.max(0, neededBasePoints - k.basePoints),
        reachable: neededRaw <= 10,
      };
    })
    .sort((a, b) => a.pointsNeeded - b.pointsNeeded);
}
